import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../foundation/altzero_base/base/components/ui/card';
import { Badge } from '../../../../foundation/altzero_base/base/components/ui/badge';
import { Alert, AlertDescription } from '../../../../foundation/altzero_base/base/components/ui/alert';
import { Input } from '../../../../foundation/altzero_base/base/components/ui/input';
import {
  Loader2, ChevronLeft, Upload, RefreshCw, AlertTriangle,
  CheckCircle, Store, Search, ChevronDown, X,
} from 'lucide-react';
import {
  getRetailers,
  getRetailerParsingPrompts,
  saveRetailerParsingPrompts,
  type Retailer,
  type RetailerParsingPrompts,
  type SectionKey,
} from '../../services/retailerService';
import { parseDocument } from '../../../parsing/services/parseDocService';
import BillPromptsTab from './BillPromptsTab';

const SECTION_LABELS: Record<SectionKey, string> = {
  energy_rates: 'Energy Rates',
  contract_terms: 'Contract Terms',
  environmental_charges: 'Environmental Charges',
  non_energy_charges: 'Non-Energy Charges',
};

const SECTION_KEYS: SectionKey[] = ['energy_rates', 'contract_terms', 'environmental_charges', 'non_energy_charges'];

const PAGE_SIZE_OPTIONS = [10, 25, 50];

type View = 'list' | 'edit';
type EditTab = 'prompts' | 'bill-prompts' | 'test';
type StatusFilter = 'all' | 'active' | 'inactive';
type PromptFilter = 'all' | 'has_prompt' | 'no_prompt';

// ─── Main Container ───────────────────────────────────────────────────────────

export const RetailerManagement: React.FC = () => {
  const [view, setView] = useState<View>('list');
  const [retailers, setRetailers] = useState<Retailer[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRetailer, setSelectedRetailer] = useState<Retailer | null>(null);
  const [editTab, setEditTab] = useState<EditTab>('prompts');

  // List filters & pagination
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [promptFilter, setPromptFilter] = useState<PromptFilter>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    loadRetailers();
  }, []);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, promptFilter, pageSize]);

  const loadRetailers = async () => {
    setLoading(true);
    const result = await getRetailers();
    if (result.success) setRetailers(result.data || []);
    setLoading(false);
  };

  const filtered = useMemo(() => {
    let list = retailers;

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(r =>
        r.name.toLowerCase().includes(q) ||
        (r.contact_email || '').toLowerCase().includes(q)
      );
    }

    if (statusFilter === 'active') list = list.filter(r => r.active);
    if (statusFilter === 'inactive') list = list.filter(r => !r.active);

    if (promptFilter === 'has_prompt') {
      list = list.filter(r => (r as any).offer_parsing_prompt);
    } else if (promptFilter === 'no_prompt') {
      list = list.filter(r => !(r as any).offer_parsing_prompt);
    }

    return list;
  }, [retailers, search, statusFilter, promptFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

  const handleEdit = (retailer: Retailer) => {
    setSelectedRetailer(retailer);
    setEditTab('prompts');
    setView('edit');
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setPromptFilter('all');
  };

  const hasActiveFilters = search || statusFilter !== 'all' || promptFilter !== 'all';

  // ── Edit View ──
  if (view === 'edit' && selectedRetailer) {
    return (
      <div>
        <button
          onClick={() => setView('list')}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ChevronLeft className="h-4 w-4" /> Back to Retailers
        </button>

        <div className="flex items-center gap-3 mb-6">
          {selectedRetailer.logo ? (
            <img src={selectedRetailer.logo} alt={selectedRetailer.name} className="h-8 w-8 object-contain rounded" />
          ) : (
            <div className="h-8 w-8 rounded bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground">
              {selectedRetailer.name[0]}
            </div>
          )}
          <h2 className="text-xl font-semibold">{selectedRetailer.name}</h2>
          <Badge variant={selectedRetailer.active ? 'default' : 'secondary'}>
            {selectedRetailer.active ? 'Active' : 'Inactive'}
          </Badge>
        </div>

        <div className="flex gap-1 mb-6 border-b">
          {(['prompts', 'bill-prompts', 'test'] as EditTab[]).map(tab => (
            <button
              key={tab}
              onClick={() => setEditTab(tab)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                editTab === tab
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab === 'prompts' ? 'Offer Parsing'
                : tab === 'bill-prompts' ? 'Bill Parsing'
                : 'Test Parser'}
            </button>
          ))}
        </div>

        {editTab === 'prompts'
          ? <PromptsTab retailer={selectedRetailer} />
          : editTab === 'bill-prompts'
          ? <BillPromptsTab retailer={selectedRetailer} />
          : <TestParserTab retailer={selectedRetailer} />
        }
      </div>
    );
  }

  // ── List View ──
  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold">Retailers</h2>
          <p className="text-sm text-muted-foreground">Configure per-retailer parsing prompts — tender offers and bill validation invoices</p>
        </div>
        <div className="text-sm text-muted-foreground">
          {filtered.length} of {retailers.length} retailer{retailers.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Filters Row */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 h-9"
          />
        </div>

        {/* Status filter */}
        <div className="relative">
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value as StatusFilter)}
            className="h-9 rounded-md border border-input bg-background px-3 pr-8 text-sm focus:outline-none focus:ring-1 focus:ring-primary appearance-none cursor-pointer"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground pointer-events-none" />
        </div>

        {/* Prompt filter */}
        <div className="relative">
          <select
            value={promptFilter}
            onChange={e => setPromptFilter(e.target.value as PromptFilter)}
            className="h-9 rounded-md border border-input bg-background px-3 pr-8 text-sm focus:outline-none focus:ring-1 focus:ring-primary appearance-none cursor-pointer"
          >
            <option value="all">All Prompts</option>
            <option value="has_prompt">Has Custom Prompt</option>
            <option value="no_prompt">No Custom Prompt</option>
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground pointer-events-none" />
        </div>

        {/* Clear filters */}
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <X className="h-3 w-3" /> Clear
          </button>
        )}

        {/* Page size */}
        <div className="ml-auto flex items-center gap-2 text-sm text-muted-foreground">
          <span>Show</span>
          <div className="relative">
            <select
              value={pageSize}
              onChange={e => setPageSize(Number(e.target.value))}
              className="h-9 rounded-md border border-input bg-background px-3 pr-8 text-sm focus:outline-none focus:ring-1 focus:ring-primary appearance-none cursor-pointer"
            >
              {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground pointer-events-none" />
          </div>
          <span>per page</span>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground py-8">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading retailers...
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <Store className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium">No retailers found</p>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="text-sm text-primary mt-2 hover:underline">
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Table */}
          <div className="rounded-md border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground w-8"></th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Retailer</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Contact</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Status</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Email Sync</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Custom Prompt</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((r, i) => (
                  <tr
                    key={r.id}
                    className={`border-b last:border-0 hover:bg-muted/30 transition-colors ${i % 2 === 0 ? '' : 'bg-muted/10'}`}
                  >
                    <td className="px-4 py-3">
                      {r.logo ? (
                        <img src={r.logo} alt={r.name} className="h-7 w-7 object-contain rounded" />
                      ) : (
                        <div className="h-7 w-7 rounded bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                          {r.name[0].toUpperCase()}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{r.name}</div>
                      {r.email_domains && r.email_domains.length > 0 && (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {r.email_domains.slice(0, 2).join(', ')}
                          {r.email_domains.length > 2 && ` +${r.email_domains.length - 2}`}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {r.contact_email || <span className="italic text-xs">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={r.active ? 'default' : 'secondary'} className="text-xs">
                        {r.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={r.sync_enabled ? 'default' : 'outline'} className="text-xs">
                        {r.sync_enabled ? 'Enabled' : 'Off'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      {(r as any).offer_parsing_prompt ? (
                        <span className="flex items-center gap-1 text-xs text-green-600">
                          <CheckCircle className="h-3 w-3" /> Custom
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">Common</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="outline" onClick={() => handleEdit(r)}>
                        Edit Prompts
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 text-sm">
              <div className="text-muted-foreground">
                Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, filtered.length)} of {filtered.length}
              </div>
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                  className="h-8 px-2 text-xs"
                >
                  «
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage(p => p - 1)}
                  disabled={page === 1}
                  className="h-8 px-2 text-xs"
                >
                  ‹
                </Button>

                {/* Page number buttons */}
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                  .reduce<(number | '...')[]>((acc, p, idx, arr) => {
                    if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push('...');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, idx) =>
                    p === '...' ? (
                      <span key={`ellipsis-${idx}`} className="px-2 text-muted-foreground">…</span>
                    ) : (
                      <Button
                        key={p}
                        size="sm"
                        variant={page === p ? 'default' : 'outline'}
                        onClick={() => setPage(p as number)}
                        className="h-8 w-8 p-0 text-xs"
                      >
                        {p}
                      </Button>
                    )
                  )
                }

                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage(p => p + 1)}
                  disabled={page === totalPages}
                  className="h-8 px-2 text-xs"
                >
                  ›
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage(totalPages)}
                  disabled={page === totalPages}
                  className="h-8 px-2 text-xs"
                >
                  »
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─── Prompts Tab ──────────────────────────────────────────────────────────────

const PromptsTab: React.FC<{ retailer: Retailer }> = ({ retailer }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flagActive, setFlagActive] = useState(false);
  const [fullPrompt, setFullPrompt] = useState('');
  const [sectionPrompts, setSectionPrompts] = useState<Partial<Record<SectionKey, string>>>({});

  useEffect(() => {
    setLoading(true);
    getRetailerParsingPrompts(retailer.id).then(result => {
      if (result.success && result.data) {
        setFlagActive(result.data.flag_active ?? false);
        setFullPrompt(result.data.offer_parsing_prompt || '');
        setSectionPrompts(result.data.section_parsing_prompts || {});
      }
      setLoading(false);
    });
  }, [retailer.id]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    const result = await saveRetailerParsingPrompts(retailer.id, {
      flag_active: flagActive,
      offer_parsing_prompt: fullPrompt.trim() || null,
      section_parsing_prompts: Object.keys(sectionPrompts).some(k => sectionPrompts[k as SectionKey])
        ? sectionPrompts
        : null,
    });
    if (result.success) {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } else {
      setError(result.error || 'Save failed');
    }
    setSaving(false);
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading prompts...</div>;
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <Card>
        <CardContent className="flex items-center justify-between py-4">
          <div>
            <p className="text-sm font-medium">Use retailer-specific prompts</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {flagActive
                ? 'Enabled — parser uses custom prompts below instead of common defaults.'
                : 'Disabled — parser uses common default prompts. Prompts saved but not used.'}
            </p>
          </div>
          <button
            onClick={() => setFlagActive(v => !v)}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none ${flagActive ? 'bg-primary' : 'bg-muted'}`}
            role="switch"
            aria-checked={flagActive}
          >
            <span className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-md transition-transform ${flagActive ? 'translate-x-5' : 'translate-x-0'}`} />
          </button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Full Offer Parsing Prompt</CardTitle>
          <p className="text-xs text-muted-foreground">
            Overrides the common prompt for <strong>{retailer.name}</strong> offers. Leave empty to use common default.
          </p>
        </CardHeader>
        <CardContent>
          <textarea
            value={fullPrompt}
            onChange={e => setFullPrompt(e.target.value)}
            placeholder="Leave empty to use the common default prompt..."
            rows={14}
            className="w-full font-mono text-xs border rounded-md p-3 resize-y bg-background focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-muted-foreground">{fullPrompt.length.toLocaleString()} characters</span>
            {fullPrompt && (
              <button onClick={() => setFullPrompt('')} className="text-xs text-muted-foreground hover:text-destructive">
                Clear
              </button>
            )}
          </div>
        </CardContent>
      </Card>

      <div>
        <h3 className="font-medium mb-1">Section Re-Parse Prompts</h3>
        <p className="text-xs text-muted-foreground mb-4">
          Used when re-parsing only a specific section. Leave empty to use the common section prompt.
        </p>
        <div className="space-y-4">
          {SECTION_KEYS.map(section => (
            <Card key={section}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">{SECTION_LABELS[section]}</CardTitle>
                  {sectionPrompts[section] && (
                    <button
                      onClick={() => setSectionPrompts(prev => { const n = { ...prev }; delete n[section]; return n; })}
                      className="text-xs text-muted-foreground hover:text-destructive"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <textarea
                  value={sectionPrompts[section] || ''}
                  onChange={e => setSectionPrompts(prev => ({
                    ...prev,
                    [section]: e.target.value || undefined,
                  }))}
                  placeholder={`Leave empty to use common ${SECTION_LABELS[section].toLowerCase()} prompt...`}
                  rows={5}
                  className="w-full font-mono text-xs border rounded-md p-3 resize-y bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <span className="text-xs text-muted-foreground">
                  {(sectionPrompts[section]?.length || 0).toLocaleString()} characters
                </span>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex items-center gap-3 pb-8">
        <Button onClick={handleSave} disabled={saving}>
          {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
          Save Prompts
        </Button>
        {saved && (
          <span className="flex items-center gap-1 text-sm text-green-600">
            <CheckCircle className="h-4 w-4" /> Saved successfully
          </span>
        )}
      </div>
    </div>
  );
};

// ─── Test Parser Tab ──────────────────────────────────────────────────────────

type SectionStatus = 'idle' | 'parsing' | 'done' | 'error';

const EMPTY_SECTION_STATUS: Record<SectionKey, SectionStatus> = {
  energy_rates: 'idle',
  contract_terms: 'idle',
  environmental_charges: 'idle',
  non_energy_charges: 'idle',
};

const TestParserTab: React.FC<{ retailer: Retailer }> = ({ retailer }) => {
  const [file, setFile] = useState<File | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [parsing, setParsing] = useState(false);
  const [parsedData, setParsedData] = useState<any>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [sectionStatus, setSectionStatus] = useState<Record<SectionKey, SectionStatus>>(EMPTY_SECTION_STATUS);
  const [retailerPrompts, setRetailerPrompts] = useState<RetailerParsingPrompts | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    getRetailerParsingPrompts(retailer.id).then(r => {
      if (r.success && r.data) setRetailerPrompts(r.data);
    });
  }, [retailer.id]);

  useEffect(() => {
    return () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); };
  }, [pdfUrl]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
      setFile(f);
      setPdfUrl(f.type === 'application/pdf' ? URL.createObjectURL(f) : null);
      setParsedData(null);
      setParseError(null);
      setSectionStatus(EMPTY_SECTION_STATUS);
    }
  };

  const handleParse = async () => {
    if (!file) return;
    setParsing(true);
    setParseError(null);
    setParsedData(null);
    setSectionStatus(EMPTY_SECTION_STATUS);

    try {
      // Server selects prompt: retailer custom (if .env flag on) → common fallback
      const result = await parseDocument(file, { retailerId: retailer.id });
      if (result.json) {
        setParsedData(result.json);
      } else {
        setParseError(result.jsonError || 'Failed to extract data from document');
      }
    } catch (e: any) {
      setParseError(e.message || 'Parse failed');
    } finally {
      setParsing(false);
    }
  };

  const handleReparse = async (section: SectionKey) => {
    if (!file) return;
    setSectionStatus(prev => ({ ...prev, [section]: 'parsing' }));

    try {
      const useRetailerPrompt = retailerPrompts?.flag_active && retailerPrompts?.section_parsing_prompts?.[section];
      let sectionPrompt: string;

      if (useRetailerPrompt) {
        sectionPrompt = retailerPrompts!.section_parsing_prompts![section]!;
      } else {
        const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';
        const res = await fetch(`${API_BASE}/api/parsing/section-prompt/${section}`);
        sectionPrompt = res.ok ? await res.text() : '';
      }

      const result = await parseDocument(file, { systemPrompt: sectionPrompt });
      if (result.json) {
        setParsedData((prev: any) => ({ ...prev, ...result.json }));
        setSectionStatus(prev => ({ ...prev, [section]: 'done' }));
      } else {
        setSectionStatus(prev => ({ ...prev, [section]: 'error' }));
      }
    } catch {
      setSectionStatus(prev => ({ ...prev, [section]: 'error' }));
    }
  };

  const promptSource = retailerPrompts?.flag_active && retailerPrompts?.offer_parsing_prompt
    ? 'retailer' : 'common';

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 220px)' }}>
      {/* Top controls */}
      <div className="flex items-center gap-4 mb-3 shrink-0">
        <div className="flex-1">
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.docx,.doc"
            onChange={handleFileChange}
            className="hidden"
            id="test-parser-upload"
          />
          <label
            htmlFor="test-parser-upload"
            className="flex items-center gap-2 cursor-pointer border-2 border-dashed rounded-lg px-4 py-3 hover:border-primary transition-colors text-sm text-muted-foreground hover:text-foreground"
          >
            <Upload className="h-4 w-4 shrink-0" />
            <span className="truncate">{file ? file.name : 'Upload offer PDF or DOCX to test'}</span>
          </label>
        </div>
        <Button onClick={handleParse} disabled={!file || parsing}>
          {parsing ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Parsing...</> : 'Parse Offer'}
        </Button>
      </div>

      {/* Prompt source indicator */}
      {file && (
        <div className="flex items-center gap-2 mb-3 text-xs text-muted-foreground shrink-0">
          <span className="rounded-full bg-muted px-2 py-0.5">
            Using <strong>{promptSource === 'retailer' ? `${retailer.name} custom` : 'common'}</strong> prompt
          </span>
        </div>
      )}

      {/* Two-pane body */}
      {file ? (
        <div className="flex gap-4 flex-1 min-h-0">
          {/* LEFT: PDF preview */}
          <div className="w-1/2 flex flex-col border rounded-lg overflow-hidden">
            <div className="text-xs text-muted-foreground px-3 py-2 border-b font-medium bg-muted/30 shrink-0">
              PDF Preview
            </div>
            {pdfUrl ? (
              <iframe src={pdfUrl} className="flex-1 w-full" title="PDF Preview" />
            ) : (
              <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
                Preview not available for this file type
              </div>
            )}
          </div>

          {/* RIGHT: Parsed fields */}
          <div className="w-1/2 overflow-y-auto space-y-3 pr-1">
            {parseError && (
              <Alert variant="destructive">
                <AlertDescription>{parseError}</AlertDescription>
              </Alert>
            )}

            {parsing && (
              <div className="flex items-center gap-2 text-muted-foreground py-8 justify-center">
                <Loader2 className="h-4 w-4 animate-spin" /> Parsing document...
              </div>
            )}

            {parsedData && (
              <>
                {SECTION_KEYS.map(section => (
                  <SectionFieldsCard
                    key={section}
                    section={section}
                    label={SECTION_LABELS[section]}
                    data={extractSectionData(parsedData, section)}
                    status={sectionStatus[section]}
                    canRegen={!!file}
                    onRegen={() => handleReparse(section)}
                    promptSource={
                      retailerPrompts?.flag_active && retailerPrompts?.section_parsing_prompts?.[section]
                        ? 'retailer'
                        : 'common'
              }
            />
                ))}

                {parsedData.notes && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-muted-foreground">Notes / Usage Estimates</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <pre className="text-xs bg-muted rounded p-3 overflow-auto max-h-40">
                        {JSON.stringify(parsedData.notes, null, 2)}
                      </pre>
                    </CardContent>
                  </Card>
                )}
              </>
            )}

            {!parsedData && !parsing && (
              <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">
                Parse the document to see extracted values
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
          Upload an offer PDF to begin testing
        </div>
      )}
    </div>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function extractSectionData(data: any, section: SectionKey): any {
  switch (section) {
    case 'energy_rates':
      return { available_terms: data.available_terms, contract_terms: data.contract_terms };
    case 'contract_terms':
      return {
        available_terms: data.available_terms,
        contract_terms: data.contract_terms,
        expiry_date: data.expiry_date,
        validity_date: data.validity_date,
      };
    case 'environmental_charges':
      return data.smooth?.environmental_charges ?? data.environmental_charges ?? null;
    case 'non_energy_charges':
      return data.smooth?.non_energy_charges ?? data.non_energy_charges ?? null;
  }
}

// ─── Section Fields Card ──────────────────────────────────────────────────────

type SectionFieldsCardProps = {
  section: SectionKey;
  label: string;
  data: any;
  status: SectionStatus;
  canRegen: boolean;
  onRegen: () => void;
  promptSource: 'retailer' | 'common';
};

const SectionFieldsCard: React.FC<SectionFieldsCardProps> = ({ section, label, data, status, canRegen, onRegen, promptSource }) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <button
            onClick={() => setExpanded(e => !e)}
            className="flex items-center gap-2 text-sm font-medium hover:text-primary text-left"
          >
            <span className="text-xs">{expanded ? '▼' : '▶'}</span>
            {label}
            {status === 'done' && <CheckCircle className="h-3 w-3 text-green-500 shrink-0" />}
            {status === 'error' && <AlertTriangle className="h-3 w-3 text-destructive shrink-0" />}
          </button>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-muted-foreground rounded-full bg-muted px-2 py-0.5">
              {promptSource} prompt
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={!canRegen || status === 'parsing'}
              onClick={onRegen}
              className="h-7 text-xs"
            >
              {status === 'parsing'
                ? <><Loader2 className="h-3 w-3 animate-spin mr-1" />Re-parsing...</>
                : <><RefreshCw className="h-3 w-3 mr-1" />Re-parse</>
              }
            </Button>
          </div>
        </div>
      </CardHeader>
      {expanded && (
        <CardContent>
          {data
            ? <SectionFields section={section} data={data} />
            : <p className="text-xs text-muted-foreground italic">No data extracted for this section.</p>
          }
        </CardContent>
      )}
    </Card>
  );
};

// ─── Field display primitive ──────────────────────────────────────────────────

const FieldBox: React.FC<{ label: string; value: string | number | null | undefined; unit?: string }> = ({ label, value, unit }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-xs text-muted-foreground">{label}</span>
    <div className="flex items-center gap-1">
      <input
        readOnly
        value={value != null ? String(value) : '—'}
        className="flex-1 text-xs border rounded px-2 py-1 bg-muted/30 min-w-0 focus:outline-none"
      />
      {unit && <span className="text-xs text-muted-foreground shrink-0">{unit}</span>}
    </div>
  </div>
);

// ─── Section-specific field renderers ─────────────────────────────────────────

const SectionFields: React.FC<{ section: SectionKey; data: any }> = ({ section, data }) => {
  switch (section) {
    case 'energy_rates':          return <EnergyRatesFields data={data} />;
    case 'contract_terms':        return <ContractTermsFields data={data} />;
    case 'environmental_charges': return <EnvironmentalChargesFields data={data} />;
    case 'non_energy_charges':    return <NonEnergyChargesFields data={data} />;
  }
};

const EnergyRatesFields: React.FC<{ data: any }> = ({ data }) => {
  if (!data?.contract_terms) return <p className="text-xs text-muted-foreground">No rate data found.</p>;
  const terms = Object.entries(data.contract_terms as Record<string, any>);

  return (
    <div className="space-y-3">
      {data.available_terms?.length > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          <span className="text-xs text-muted-foreground mr-1">Available terms:</span>
          {(data.available_terms as string[]).map(t => (
            <Badge key={t} variant="outline" className="text-xs px-1.5 py-0">{t} mo</Badge>
          ))}
        </div>
      )}
      {terms.map(([termKey, term]) => {
        const years = Object.entries(term as Record<string, any>)
          .filter(([k]) => k.startsWith('year_'))
          .sort(([a], [b]) => a.localeCompare(b));
        return (
          <div key={termKey} className="border rounded-md p-3 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold">{termKey} months</span>
              <Badge variant={term.rate_type === 'Smooth' ? 'default' : 'secondary'} className="text-xs px-1.5 py-0">
                {term.rate_type}
              </Badge>
              {term.date_range && (
                <span className="text-xs text-muted-foreground">
                  {term.date_range.start_date} → {term.date_range.end_date}
                </span>
              )}
            </div>
            {years.map(([yearKey, year]) => (
              <div key={yearKey} className="pl-2 border-l-2 border-muted space-y-1">
                <div className="flex items-center gap-1 mb-1">
                  <span className="text-xs font-medium text-muted-foreground capitalize">{yearKey}</span>
                  {year.date_range && (
                    <span className="text-xs text-muted-foreground">
                      ({year.date_range.start_date} → {year.date_range.end_date})
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <FieldBox label="Peak" value={year.peak} unit="c/kWh" />
                  <FieldBox label="Off-peak" value={year.offpeak} unit="c/kWh" />
                  <FieldBox label="Shoulder" value={year.shoulder} unit="c/kWh" />
                </div>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

const ContractTermsFields: React.FC<{ data: any }> = ({ data }) => {
  const terms = Object.entries((data.contract_terms || {}) as Record<string, any>);
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <FieldBox label="Expiry Date" value={data.expiry_date} />
        <FieldBox label="Validity Date" value={data.validity_date} />
      </div>
      {terms.map(([key, term]) => (
        <div key={key} className="border rounded-md p-2 flex items-center gap-3 flex-wrap">
          <span className="text-xs font-semibold w-20">{key} mo</span>
          <Badge variant={term.rate_type === 'Smooth' ? 'default' : 'secondary'} className="text-xs px-1.5 py-0">
            {term.rate_type}
          </Badge>
          {term.date_range && (
            <span className="text-xs text-muted-foreground">
              {term.date_range.start_date} → {term.date_range.end_date}
            </span>
          )}
        </div>
      ))}
    </div>
  );
};

const EnvironmentalChargesFields: React.FC<{ data: any }> = ({ data }) => {
  if (!data || typeof data !== 'object') return <p className="text-xs text-muted-foreground">No environmental charge data found.</p>;
  const schemes = Object.entries(data as Record<string, any>);
  return (
    <div className="space-y-3">
      {schemes.map(([scheme, years]) => {
        const yearEntries = Object.entries(years as Record<string, any>)
          .filter(([k]) => k.startsWith('year_'))
          .sort(([a], [b]) => a.localeCompare(b));
        return (
          <div key={scheme} className="border rounded-md p-3">
            <div className="text-xs font-semibold uppercase mb-2 text-primary">{scheme}</div>
            <div className="space-y-2">
              {yearEntries.map(([yearKey, year]) => (
                <div key={yearKey} className="pl-2 border-l-2 border-muted">
                  <div className="flex items-center gap-1 mb-1">
                    <span className="text-xs text-muted-foreground capitalize">{yearKey}</span>
                    {year.date_range && (
                      <span className="text-xs text-muted-foreground">
                        ({year.date_range.start_date} → {year.date_range.end_date})
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <FieldBox label="Rate" value={year.rate_per_kwh} unit="c/kWh" />
                    <FieldBox label="Cert Price" value={year.price_per_certificate} unit="$/cert" />
                    <FieldBox label="Power %" value={year.power_percentage} unit="%" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const NonEnergyChargesFields: React.FC<{ data: any }> = ({ data }) => {
  const fmt = (v: number | null) => v != null ? `$${v.toLocaleString()}` : null;
  return (
    <div className="grid grid-cols-2 gap-3">
      <FieldBox label="Service Charge" value={fmt(data.service_charge)} unit="/yr" />
      <FieldBox label="Supplementary" value={fmt(data.supplementary_metering)} unit="/yr" />
      <FieldBox label="Market Fees" value={fmt(data.market_fees)} unit="/yr" />
      <FieldBox label="Meter Agreement" value={fmt(data.meter_agreement)} unit="/yr" />
    </div>
  );
};

export default RetailerManagement;
